import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useStore } from '@tanstack/react-store'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Check,
  Coins,
  Gavel,
  Grid2x2,
  Handshake,
  Loader2,
  Package,
  Pencil,
  Plus,
  Signpost,
  Tag,
  Trash2,
  Users,
  X,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { NumberInput } from '@/components/ui/number-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AppShell } from '../../components/AppShell'
import { BadgeToggleGroup } from '../../components/BadgeToggleGroup'
import { ChipMultiSelect } from '../../components/ChipMultiSelect'
import { CurrencyAmountInput } from '../../components/CurrencyAmountInput'
import { DetailList, DetailRow } from '../../components/DetailList'
import { FieldLabel } from '../../components/FieldLabel'
import { FormSkeleton } from '../../components/FormSkeleton'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { LabelAutocomplete } from '../../components/LabelAutocomplete'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { StatsBar, StatsBarActivity, StatsBarId } from '../../components/StatsBar'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { CappingCard } from '../../components/capping/CappingCard'
import { TargetingCard } from '../../components/targeting/TargetingCard'
import { useEditMode } from '../../hooks/useEditMode'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { useNetwork } from '../../contexts/NetworkContext'
import { AuthService } from '@/services/core/auth'
import { apiErrorDescription } from '@/services/core/apiError'
import {
  AX_PRODUCT_TYPES,
  AxProductsApi,
  BID_STRATEGY_OPTIONS,
  BUDGET_SPECIFICATIONS,
  CPM_SPECIFICATIONS,
  LINE_ITEM_WORKFLOW_TYPES,
  OBJECTIVE_SPECIFICATIONS,
  type AxProduct,
  type AxProductInput,
  type AxProductType,
  type BidStrategyOption,
  type BudgetSpecification,
  type CpmSpecification,
  type LineItemWorkflowType,
  type ObjectiveSpecification,
} from '@/services/earnings/axProductsApi'
import { LayoutsApi } from '@/services/sites/layoutsApi'
import { TeamsApi } from '@/services/users/teamsApi'
import { DELIVERY_CURVES, type CurrencyAmount, type DeliveryCurve } from '@/services/lineItems/lineItemsApi'
import {
  AX_PRODUCT_RATE_LIMIT_TYPES,
  hasRateLimitProblem,
  rateLimitsForSave,
} from '@/services/earnings/rateLimits'
import {
  TARGETING_TYPES,
  hiddenTargetingTypes,
  targetingForSave,
  type Targeting,
} from '@/services/targeting/targeting'
import { formatDate } from '../../utils/format'
import { ValidationWarnings } from '@/components/ValidationWarnings'
import { ObjectStateToggle, useSaveObjectState, type ObjectState } from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'

/** Who may edit the product — the network's admins, or one publisher team's. */
type Ownership = 'NETWORK' | 'TEAM'

interface DimensionEntry {
  width: number | null
  height: number | null
}

/** Money as the form holds it: a string, so a half-typed amount survives. */
function amountText(value: CurrencyAmount | undefined): string {
  return value?.amount != null ? String(value.amount) : ''
}

/** The money field as the API wants it, or `null` to clear what is stored. */
function moneyForSave(text: string, currency: string): CurrencyAmount | null {
  const amount = Number(text)
  if (!text.trim() || Number.isNaN(amount)) return null
  return { currency, amount }
}

// ── Inner form ──────────────────────────────────────────────────────────────

interface ProductEditFormProps {
  product: AxProduct | null
  id: string
  isNew: boolean
}

function ProductEditForm({ product, id, isNew }: ProductEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()
  const { isEditing, startEditing, stopEditing } = useEditMode(isNew)

  const { data: advertiserTeams = [] } = useQuery({
    queryKey: ['teams', 'AX_ADVERTISER'],
    queryFn: () => TeamsApi.getByType('AX_ADVERTISER'),
    staleTime: 5 * 60 * 1000,
  })
  const { data: publisherTeams = [] } = useQuery({
    queryKey: ['teams', 'AX_PUBLISHER'],
    queryFn: () => TeamsApi.getByType('AX_PUBLISHER'),
    staleTime: 5 * 60 * 1000,
  })
  const { data: layouts = [] } = useQuery({
    queryKey: ['layouts'],
    queryFn: () => LayoutsApi.getAll(),
    staleTime: 5 * 60 * 1000,
  })

  // One currency covers every amount on a product, so the fields share it.
  const storedCurrency =
    product?.minimumCpm?.currency ??
    product?.minimumCpc?.currency ??
    product?.minimumBudget?.currency ??
    product?.maximumBudget?.currency
  const fallbackCurrency = currentNetwork?.defaultCurrency || 'USD'

  const tiers = currentNetwork?.tiers ?? []

  const form = useForm({
    defaultValues: {
      name: product?.name ?? '',
      description: product?.description ?? '',
      type: (product?.type ?? 'STANDARD') as AxProductType,
      objectiveSpecification: (product?.objectiveSpecification ?? 'ANY') as ObjectiveSpecification,
      cpmSpecification: (product?.cpmSpecification ?? 'UNCAPPED') as CpmSpecification,
      budgetSpecification: (product?.budgetSpecification ?? 'ALL_OBJECTIVES') as BudgetSpecification,
      bidStrategyOption: (product?.bidStrategyOption ?? 'STANDARD_ONLY') as BidStrategyOption,
      lineItemWorkflowType: (product?.lineItemWorkflowType ?? 'DEFAULT') as LineItemWorkflowType,
      currency: storedCurrency ?? fallbackCurrency,
      minimumCpm: amountText(product?.minimumCpm),
      minimumCpc: amountText(product?.minimumCpc),
      minimumBudget: amountText(product?.minimumBudget),
      maximumBudget: amountText(product?.maximumBudget),
      smoothingChoices: (product?.smoothingChoices ?? []) as DeliveryCurve[],
      dealId: product?.dealId ?? '',
      presentationIndex: product?.presentationIndex ?? 0,
      defaultTierId: product?.defaultTier?.id ?? '',
      ownership: (product?.ownerTeam ? 'TEAM' : 'NETWORK') as Ownership,
      ownerTeamId: product?.ownerTeam?.id ?? '',
      teamIds: product?.teams.map((team) => team.id) ?? [],
      layoutIds: product?.layouts.map((l) => l.id) ?? [],
      labels: product?.labels ?? [],
      dimensions: (product?.allowedDimensions ?? []).map(([width, height]) => ({
        width,
        height,
      })) as DimensionEntry[],
      targeting: (product?.targeting ?? {}) as Targeting,
      optionalTargetTypes: product?.optionalTargetTypes ?? [],
      rateLimits: product?.rateLimits ?? [],
    },
    onSubmit: ({ value }) => {
      mutation.mutate({
        name: value.name,
        description: value.description,
        objectState: (product?.objectState ?? 'ACTIVE') as ObjectState,
        type: value.type,
        objectiveSpecification: value.objectiveSpecification,
        cpmSpecification: value.cpmSpecification,
        budgetSpecification: value.budgetSpecification,
        bidStrategyOption: value.bidStrategyOption,
        lineItemWorkflowType: value.lineItemWorkflowType,
        minimumCpm: moneyForSave(value.minimumCpm, value.currency),
        minimumCpc: moneyForSave(value.minimumCpc, value.currency),
        minimumBudget: moneyForSave(value.minimumBudget, value.currency),
        maximumBudget: moneyForSave(value.maximumBudget, value.currency),
        smoothingChoices: value.smoothingChoices,
        dealId: value.dealId.trim() || null,
        presentationIndex: value.presentationIndex ?? 0,
        defaultTier: value.defaultTierId || null,
        // A network-owned product has no owner team, and saying so is how an
        // existing team owner is removed.
        ownerTeam: value.ownership === 'TEAM' ? value.ownerTeamId || null : null,
        teams: value.teamIds,
        layouts: value.layoutIds,
        labels: value.labels,
        allowedDimensions: value.dimensions
          .filter((d) => d.width != null && d.height != null)
          .map((d) => [d.width!, d.height!]),
        targeting: targetingForSave(value.targeting),
        optionalTargetTypes: value.optionalTargetTypes,
        rateLimits: rateLimitsForSave(value.rateLimits, value.currency),
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => AxProductsApi.update(id, { name: product?.name ?? '', objectState }),
    ['axproduct', id],
    ['axproducts']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const mutation = useMutation({
    mutationFn: (input: AxProductInput) =>
      isNew ? AxProductsApi.create(id, input) : AxProductsApi.update(id, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['axproduct', id], data)
      queryClient.invalidateQueries({ queryKey: ['axproducts-all'] })
      queryClient.invalidateQueries({ queryKey: ['axproducts'] })
      markClean()
      if (isNew) {
        navigate(`/admin/products/${id}`, { replace: true })
      } else {
        stopEditing()
      }
      toast.success(t('common.saved'), {
        description: isNew ? t('products.createdSuccess') : t('products.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      toast.error(isNew ? t('products.createFailed') : t('products.saveFailed'), {
        description: apiErrorDescription(error),
        duration: 5000,
      })
    },
  })

  const values = useStore(form.store, (s) => s.values)
  const capsBroken = hasRateLimitProblem(values.rateLimits)

  const typeOptions = AX_PRODUCT_TYPES.filter(
    // Political products only make sense on a political network, but one that
    // is already political stays editable wherever it is opened.
    (type) => type !== 'POLITICAL' || currentNetwork?.politicalApp || product?.type === 'POLITICAL'
  )
  const bidStrategyOptions = BID_STRATEGY_OPTIONS.filter(
    // Dynamic bidding is a marketplace feature.
    (option) =>
      (option !== 'ALLOW_DYNAMIC' && option !== 'ALLOW_ALL') ||
      currentNetwork?.marketplacePlatform ||
      product?.bidStrategyOption === option
  )

  const setDimension = (index: number, field: keyof DimensionEntry, value: number | null) =>
    form.setFieldValue(
      'dimensions',
      values.dimensions.map((d, i) => (i === index ? { ...d, [field]: value } : d))
    )

  const crumbs = [
    { label: t('products.title'), href: '/admin/products' },
    { label: isNew ? t('products.newProduct') : (product?.name ?? t('products.editTitle')) },
  ]

  const ownerTeamName =
    publisherTeams.find((team) => team.id === values.ownerTeamId)?.name ??
    product?.ownerTeam?.name ??
    values.ownerTeamId

  return (
    <AppShell crumbs={crumbs} showBack objectType={isNew ? undefined : t('objectTypes.product')}>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={product?.validationWarnings} />

        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.product')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('products.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {!isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
                <ObjectStateToggle value={product!.objectState ?? 'ACTIVE'} onChange={saveObjectState} />
              {!isEditing && (
                <Button type="button" variant="outline" onClick={startEditing}>
                  <Pencil className="size-3.5" />
                  {t('common.edit')}
                </Button>
              )}
            </div>
          )}
        </div>

        {product && !isNew && (
          <StatsBar
            items={[
              { label: t('common.id'), content: <StatsBarId value={product.id} /> },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={
                      product.createTime
                        ? formatDate(product.createTime)
                        : ''
                    }
                    updated={
                      product.updateTime
                        ? formatDate(product.updateTime)
                        : undefined
                    }
                  />
                ),
              },
            ]}
          />
        )}

        {isEditing ? (
          <div className="flex flex-wrap gap-3.5 items-start">
            {/* Left column — what the product is and what it costs */}
            <div className="flex flex-col gap-3.5 flex-1 min-w-80">
              <Card>
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Package} />
                    {t('common.details')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="field-grid">
                    <div className="p-field full">
                      <FieldLabel>{t('common.description')}</FieldLabel>
                      <form.Field name="description">
                        {(field) => (
                          <Textarea
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder={t('products.descriptionPlaceholder')}
                            rows={2}
                            className="resize-y"
                          />
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel tooltip={typeOptions.map((o) => t(`products.typeHelp_${o}`)).join('\n')}>
                        {t('common.type')}
                      </FieldLabel>
                      <form.Field name="type">
                        {(field) => (
                          <Select
                            label={t('common.type')}
                            value={field.state.value}
                            onValueChange={(v) => v && field.handleChange(v as AxProductType)}
                          >
                            <SelectTrigger>
                              <SelectValue>{t(`products.type_${field.state.value}`)}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {typeOptions.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {t(`products.type_${option}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel
                        tooltip={OBJECTIVE_SPECIFICATIONS.map((o) =>
                          t(`products.objectiveSpecificationHelp_${o}`)
                        ).join('\n')}
                      >
                        {t('products.objectiveRequirements')}
                      </FieldLabel>
                      <form.Field name="objectiveSpecification">
                        {(field) => (
                          <Select
                            label={t('products.objectiveRequirements')}
                            value={field.state.value}
                            onValueChange={(v) => v && field.handleChange(v as ObjectiveSpecification)}
                          >
                            <SelectTrigger>
                              <SelectValue>
                                {t(`products.objectiveSpecification_${field.state.value}`)}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {OBJECTIVE_SPECIFICATIONS.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {t(`products.objectiveSpecification_${option}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel
                        tooltip={LINE_ITEM_WORKFLOW_TYPES.map((o) =>
                          t(`products.workflowHelp_${o}`)
                        ).join('\n')}
                      >
                        {t('products.reviewWorkflow')}
                      </FieldLabel>
                      <form.Field name="lineItemWorkflowType">
                        {(field) => (
                          <Select
                            label={t('products.reviewWorkflow')}
                            value={field.state.value}
                            onValueChange={(v) => v && field.handleChange(v as LineItemWorkflowType)}
                          >
                            <SelectTrigger>
                              <SelectValue>
                                {t(`products.workflow_${field.state.value}`)}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {LINE_ITEM_WORKFLOW_TYPES.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {t(`products.workflow_${option}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel tooltip={t('products.defaultTierHelp')}>
                        {t('products.defaultTier')}
                      </FieldLabel>
                      <form.Field name="defaultTierId">
                        {(field) => (
                          <Select
                            label={t('products.defaultTier')}
                            value={field.state.value}
                            onValueChange={(v) => field.handleChange(v ?? '')}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={t('products.noTier')}>
                                {tiers.find((tier) => tier.id === field.state.value)?.name ??
                                  t('products.noTier')}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {tiers.map((tier) => (
                                <SelectItem key={tier.id} value={tier.id}>
                                  {tier.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel tooltip={t('products.dealIdHelp')}>
                        {t('products.dealId')}
                      </FieldLabel>
                      <form.Field name="dealId">
                        {(field) => (
                          <Input
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder={t('products.dealIdPlaceholder')}
                          />
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel tooltip={t('products.presentationIndexHelp')}>
                        {t('products.presentationIndex')}
                      </FieldLabel>
                      <form.Field name="presentationIndex">
                        {(field) => (
                          <NumberInput
                            value={field.state.value}
                            onChange={(v) => field.handleChange(v ?? 0)}
                            min={0}
                          />
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field full">
                      <FieldLabel>{t('common.labels')}</FieldLabel>
                      <form.Field name="labels">
                        {(field) => (
                          <LabelAutocomplete
                            value={field.state.value}
                            onChange={(v) => field.handleChange(v)}
                          />
                        )}
                      </form.Field>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card tone="money">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Coins} />
                    {t('products.pricing')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="field-grid">
                    <div className="p-field">
                      <FieldLabel tooltip={t('products.minimumCpmHelp')}>
                        {t('products.minimumCpm')}
                      </FieldLabel>
                      <form.Field name="minimumCpm">
                        {(field) => (
                          <form.Field name="currency">
                            {(currencyField) => (
                              <CurrencyAmountInput
                                value={field.state.value}
                                onChange={(v) => field.handleChange(v)}
                                currency={currencyField.state.value}
                                onCurrencyChange={(c) => currencyField.handleChange(c)}
                                currencyFallback={fallbackCurrency}
                              />
                            )}
                          </form.Field>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel
                        tooltip={CPM_SPECIFICATIONS.map((o) =>
                          t(`products.cpmSpecificationHelp_${o}`)
                        ).join('\n')}
                      >
                        {t('products.cpmValue')}
                      </FieldLabel>
                      <form.Field name="cpmSpecification">
                        {(field) => (
                          <Select
                            label={t('products.cpmValue')}
                            value={field.state.value}
                            onValueChange={(v) => v && field.handleChange(v as CpmSpecification)}
                          >
                            <SelectTrigger>
                              <SelectValue>
                                {t(`products.cpmSpecification_${field.state.value}`)}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {CPM_SPECIFICATIONS.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {t(`products.cpmSpecification_${option}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel tooltip={t('products.minimumCpcHelp')}>
                        {t('products.minimumCpc')}
                      </FieldLabel>
                      <form.Field name="minimumCpc">
                        {(field) => (
                          <form.Field name="currency">
                            {(currencyField) => (
                              <CurrencyAmountInput
                                value={field.state.value}
                                onChange={(v) => field.handleChange(v)}
                                currency={currencyField.state.value}
                                onCurrencyChange={(c) => currencyField.handleChange(c)}
                                currencyFallback={fallbackCurrency}
                              />
                            )}
                          </form.Field>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel
                        tooltip={BUDGET_SPECIFICATIONS.map((o) =>
                          t(`products.budgetSpecificationHelp_${o}`)
                        ).join('\n')}
                      >
                        {t('products.budgetSpecification')}
                      </FieldLabel>
                      <form.Field name="budgetSpecification">
                        {(field) => (
                          <Select
                            label={t('products.budgetSpecification')}
                            value={field.state.value}
                            onValueChange={(v) => v && field.handleChange(v as BudgetSpecification)}
                          >
                            <SelectTrigger>
                              <SelectValue>
                                {t(`products.budgetSpecification_${field.state.value}`)}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {BUDGET_SPECIFICATIONS.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {t(`products.budgetSpecification_${option}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel tooltip={t('products.minimumBudgetHelp')}>
                        {t('products.minimumBudget')}
                      </FieldLabel>
                      <form.Field name="minimumBudget">
                        {(field) => (
                          <form.Field name="currency">
                            {(currencyField) => (
                              <CurrencyAmountInput
                                value={field.state.value}
                                onChange={(v) => field.handleChange(v)}
                                currency={currencyField.state.value}
                                onCurrencyChange={(c) => currencyField.handleChange(c)}
                                currencyFallback={fallbackCurrency}
                              />
                            )}
                          </form.Field>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <FieldLabel tooltip={t('products.maximumBudgetHelp')}>
                        {t('products.maximumBudget')}
                      </FieldLabel>
                      <form.Field name="maximumBudget">
                        {(field) => (
                          <form.Field name="currency">
                            {(currencyField) => (
                              <CurrencyAmountInput
                                value={field.state.value}
                                onChange={(v) => field.handleChange(v)}
                                currency={currencyField.state.value}
                                onCurrencyChange={(c) => currencyField.handleChange(c)}
                                currencyFallback={fallbackCurrency}
                              />
                            )}
                          </form.Field>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field full">
                      <FieldLabel
                        tooltip={bidStrategyOptions
                          .map((o) => t(`products.bidStrategyOptionHelp_${o}`))
                          .join('\n')}
                      >
                        {t('products.bidStrategy')}
                      </FieldLabel>
                      <form.Field name="bidStrategyOption">
                        {(field) => (
                          <Select
                            label={t('products.bidStrategy')}
                            value={field.state.value}
                            onValueChange={(v) => v && field.handleChange(v as BidStrategyOption)}
                          >
                            <SelectTrigger>
                              <SelectValue>
                                {t(`products.bidStrategyOption_${field.state.value}`)}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {bidStrategyOptions.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {t(`products.bidStrategyOption_${option}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field full">
                      <form.Field name="smoothingChoices">
                        {(field) => (
                          <BadgeToggleGroup
                            label={t('products.smoothingChoices')}
                            options={DELIVERY_CURVES.map((curve) => ({
                              label: t(`lineItems.deliveryCurves.${curve}`),
                              value: curve,
                            }))}
                            value={field.state.value}
                            onChange={(v) => field.handleChange(v as DeliveryCurve[])}
                          />
                        )}
                      </form.Field>
                      <span className="text-xs text-(--ad-text-muted)">
                        {t('products.smoothingChoicesHint')}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card tone="people">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Users} />
                    {t('products.access')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="field-grid">
                    <div className="p-field">
                      <FieldLabel
                        tooltip={(['NETWORK', 'TEAM'] as Ownership[])
                          .map((o) => t(`products.ownershipHelp_${o}`))
                          .join('\n')}
                      >
                        {t('products.ownership')}
                      </FieldLabel>
                      <form.Field name="ownership">
                        {(field) => (
                          <Select
                            label={t('products.ownership')}
                            value={field.state.value}
                            onValueChange={(v) => v && field.handleChange(v as Ownership)}
                          >
                            <SelectTrigger>
                              <SelectValue>
                                {t(`products.ownership_${field.state.value}`)}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {(['NETWORK', 'TEAM'] as Ownership[]).map((option) => (
                                <SelectItem key={option} value={option}>
                                  {t(`products.ownership_${option}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </form.Field>
                    </div>
                    {values.ownership === 'TEAM' && (
                      <div className="p-field">
                        <FieldLabel>{t('products.ownerTeam')}</FieldLabel>
                        <form.Field name="ownerTeamId">
                          {(field) => (
                            <Select
                              label={t('products.ownerTeam')}
                              value={field.state.value}
                              onValueChange={(v) => field.handleChange(v ?? '')}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={t('products.selectOwnerTeam')}>
                                  {publisherTeams.find((team) => team.id === field.state.value)
                                    ?.name ?? t('products.selectOwnerTeam')}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {publisherTeams.map((team) => (
                                  <SelectItem key={team.id} value={team.id}>
                                    {team.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </form.Field>
                      </div>
                    )}
                    <div className="p-field full">
                      <form.Field name="teamIds">
                        {(field) => (
                          <BadgeToggleGroup
                            label={t('products.advertiserTeams')}
                            options={advertiserTeams.map((team) => ({
                              label: team.name,
                              value: team.id,
                            }))}
                            value={field.state.value}
                            onChange={(v) => field.handleChange(v)}
                            emptyText={t('products.noAdvertiserTeams')}
                          />
                        )}
                      </form.Field>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right column — what may run on the product */}
            <div className="flex flex-col gap-3.5 flex-1 min-w-80">
              <Card tone="creative">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Grid2x2} />
                    {t('products.creativeRules')}
                  </CardTitle>
                  <CardAction>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        form.setFieldValue('dimensions', [
                          ...values.dimensions,
                          { width: null, height: null },
                        ])
                      }
                    >
                      <Plus className="size-4" /> {t('products.addDimensions')}
                    </Button>
                  </CardAction>
                </CardHeader>
                <CardContent>
                  <div className="field-grid">
                    <div className="p-field full">
                      <FieldLabel tooltip={t('products.layoutsHelp')}>
                        {t('products.layouts')}
                      </FieldLabel>
                      <form.Field name="layoutIds">
                        {(field) => (
                          <ChipMultiSelect
                            value={field.state.value}
                            onChange={(e) => field.handleChange((e.value as string[]) ?? [])}
                            options={layouts}
                            optionLabel="name"
                            optionValue="id"
                            placeholder={t('products.selectLayouts')}
                          />
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field full">
                      <FieldLabel hint={t('products.dimensionsHint')}>
                        {t('products.dimensions')}
                      </FieldLabel>
                      {values.dimensions.length === 0 ? (
                        <span className="text-xs text-(--ad-text-muted)">
                          {t('products.anyDimensions')}
                        </span>
                      ) : (
                        <div className="flex flex-col gap-2">
                          {values.dimensions.map((entry, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <NumberInput
                                value={entry.width}
                                onChange={(v) => setDimension(i, 'width', v ?? null)}
                                min={0}
                                placeholder={t('products.width')}
                              />
                              <span className="text-muted-foreground">×</span>
                              <NumberInput
                                value={entry.height}
                                onChange={(v) => setDimension(i, 'height', v ?? null)}
                                min={0}
                                placeholder={t('products.height')}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                title={t('common.remove')}
                                onClick={() =>
                                  form.setFieldValue(
                                    'dimensions',
                                    values.dimensions.filter((_, index) => index !== i)
                                  )
                                }
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <form.Field name="targeting">
                {(field) => (
                  <TargetingCard
                    targeting={field.state.value}
                    onChange={(targeting) => field.handleChange(targeting)}
                    hiddenTypes={hiddenTargetingTypes(currentNetwork)}
                  />
                )}
              </form.Field>

              <Card tone="delivery">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Signpost} />
                    {t('products.optionalTargeting')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form.Field name="optionalTargetTypes">
                    {(field) => (
                      <BadgeToggleGroup
                        options={TARGETING_TYPES.map((def) => ({
                          label: t(`targeting.types.${def.id}.plural`),
                          value: def.apiType,
                        }))}
                        value={field.state.value}
                        onChange={(v) => field.handleChange(v)}
                      />
                    )}
                  </form.Field>
                  <span className="text-xs text-(--ad-text-muted)">
                    {t('products.optionalTargetingHint')}
                  </span>
                </CardContent>
              </Card>

              <form.Field name="rateLimits">
                {(field) => (
                  <CappingCard
                    value={field.state.value}
                    onChange={(limits) => field.handleChange(limits)}
                    types={AX_PRODUCT_RATE_LIMIT_TYPES}
                    currency={values.currency}
                  />
                )}
              </form.Field>
            </div>
          </div>
        ) : (
          product && (
            <div className="flex flex-wrap gap-3.5 items-start">
              <div className="flex flex-col gap-3.5 flex-1 min-w-80">
                <Card>
                  <CardHeader>
                    <CardTitle>
                      <CardIcon icon={Package} />
                      {t('common.details')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DetailList>
                      <DetailRow label={t('common.type')} icon={Package}>
                        {t(`products.type_${product.type}`)}
                      </DetailRow>
                      <DetailRow label={t('common.description')}>
                        {product.description}
                      </DetailRow>
                      <DetailRow label={t('products.objectiveRequirements')}>
                        {t(`products.objectiveSpecification_${product.objectiveSpecification}`)}
                      </DetailRow>
                      <DetailRow label={t('products.reviewWorkflow')}>
                        {t(`products.workflow_${product.lineItemWorkflowType}`)}
                      </DetailRow>
                      <DetailRow label={t('products.defaultTier')}>
                        {product.defaultTier?.name}
                      </DetailRow>
                      <DetailRow label={t('products.dealId')}>{product.dealId}</DetailRow>
                      <DetailRow label={t('products.presentationIndex')}>
                        {product.presentationIndex}
                      </DetailRow>
                      <DetailRow label={t('common.labels')} icon={Tag}>
                        {product.labels.join(', ')}
                      </DetailRow>
                    </DetailList>
                  </CardContent>
                </Card>

                <Card tone="money">
                  <CardHeader>
                    <CardTitle>
                      <CardIcon icon={Coins} />
                      {t('products.pricing')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DetailList>
                      <DetailRow label={t('products.minimumCpm')}>
                        {product.minimumCpm &&
                          `${product.minimumCpm.amount} ${product.minimumCpm.currency}`}
                      </DetailRow>
                      <DetailRow label={t('products.cpmValue')}>
                        {t(`products.cpmSpecification_${product.cpmSpecification}`)}
                      </DetailRow>
                      <DetailRow label={t('products.minimumCpc')}>
                        {product.minimumCpc &&
                          `${product.minimumCpc.amount} ${product.minimumCpc.currency}`}
                      </DetailRow>
                      <DetailRow label={t('products.budgetSpecification')}>
                        {t(`products.budgetSpecification_${product.budgetSpecification}`)}
                      </DetailRow>
                      <DetailRow label={t('products.minimumBudget')}>
                        {product.minimumBudget &&
                          `${product.minimumBudget.amount} ${product.minimumBudget.currency}`}
                      </DetailRow>
                      <DetailRow label={t('products.maximumBudget')}>
                        {product.maximumBudget &&
                          `${product.maximumBudget.amount} ${product.maximumBudget.currency}`}
                      </DetailRow>
                      <DetailRow label={t('products.bidStrategy')} icon={Gavel}>
                        {t(`products.bidStrategyOption_${product.bidStrategyOption ?? 'STANDARD_ONLY'}`)}
                      </DetailRow>
                      <DetailRow label={t('products.smoothingChoices')}>
                        {product.smoothingChoices.length > 0
                          ? product.smoothingChoices
                              .map((curve) => t(`lineItems.deliveryCurves.${curve}`))
                              .join(', ')
                          : t('products.allDeliveryCurves')}
                      </DetailRow>
                    </DetailList>
                  </CardContent>
                </Card>
              </div>

              <div className="flex flex-col gap-3.5 flex-1 min-w-80">
                <Card tone="people">
                  <CardHeader>
                    <CardTitle>
                      <CardIcon icon={Handshake} />
                      {t('products.access')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DetailList>
                      <DetailRow label={t('products.owner')} icon={Users}>
                        {product.ownerTeam ? ownerTeamName : t('products.ownerNetwork')}
                      </DetailRow>
                      <DetailRow label={t('products.advertiserTeams')}>
                        {product.teams.map((team) => team.name).join(', ')}
                      </DetailRow>
                      <DetailRow label={t('products.layouts')} icon={Grid2x2}>
                        {product.layouts.map((l) => l.name).join(', ')}
                      </DetailRow>
                      <DetailRow label={t('products.dimensions')}>
                        {product.allowedDimensions.length > 0
                          ? product.allowedDimensions.map((d) => d.join('×')).join(', ')
                          : t('products.anyDimensions')}
                      </DetailRow>
                      <DetailRow label={t('products.optionalTargeting')} icon={Signpost}>
                        {TARGETING_TYPES.filter((def) =>
                          product.optionalTargetTypes.includes(def.apiType)
                        )
                          .map((def) => t(`targeting.types.${def.id}.plural`))
                          .join(', ')}
                      </DetailRow>
                    </DetailList>
                  </CardContent>
                </Card>

                <form.Field name="targeting">
                  {(field) => (
                    <TargetingCard
                      targeting={field.state.value}
                      onChange={(targeting) => field.handleChange(targeting)}
                      hiddenTypes={hiddenTargetingTypes(currentNetwork)}
                      // Outside edit mode, applying targeting saves the
                      // product there and then.
                      onApplied={() => form.handleSubmit()}
                    />
                  )}
                </form.Field>

                <CappingCard
                  value={product.rateLimits}
                  onChange={() => {}}
                  types={AX_PRODUCT_RATE_LIMIT_TYPES}
                  currency={storedCurrency ?? fallbackCurrency}
                  readOnly
                  readOnlyReason={t('products.cappingReadOnly')}
                />
              </div>
            </div>
          )
        )}

        <div
          className={`detail-savebar${isEditing || isDirty || mutation.isPending ? ' is-visible' : ''}`}
        >
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
            {!mutation.isPending && capsBroken && (
              <span className="text-(--ad-danger)">{t('products.fixCaps')}</span>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => (isNew ? navigate('/admin/products') : stopEditing())}
              disabled={mutation.isPending}
            >
              <X className="size-4" />
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending} disabled={capsBroken}>
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('products.createProduct') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function ProductEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const {
    data: product,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['axproduct', id],
    queryFn: () => AxProductsApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbsLoading = [
    { label: t('products.title'), href: '/admin/products' },
    { label: isNew ? t('products.newProduct') : t('products.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={4} columns={2} />
      </AppShell>
    )
  }

  return <ProductEditForm key={id} product={product ?? null} id={id!} isNew={isNew} />
}
